import axios from 'axios';

const apiClient = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  const method = config.method?.toUpperCase();
  const needsCsrfHeader = ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method);
  
  if (needsCsrfHeader) {
    config.headers['X-App-Intent'] = 'true';
  }

  return config;
});

let logoutFunction = null;
export const setLogoutFunction = (logout) => {
  logoutFunction = logout;
};

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && logoutFunction) {
      logoutFunction();
    }
    
    return Promise.reject(error);
  }
);

const authAPI = {
  checkStatus: () => apiClient.get('/auth/check-status'),
  googleLogin: (token) => apiClient.post('/auth/google-login', { IdToken: token }),
  logout: () => apiClient.post('/auth/logout'),
};

const userAPI = {
  getUsers: (roles = []) => 
    apiClient.get('users',  { params: new URLSearchParams(roles.map(role => ['roles', role])) }),

  addUser: (userData) =>
    apiClient.post('users', userData),

  deleteUser: (userGuid) => 
    apiClient.delete(`users/${userGuid}`),

  addUserRole: (userGuid, role) =>
    apiClient.post(`users/${userGuid}/roles`, { role }),

  removeUserRole: (userGuid, role) =>
    apiClient.delete(`users/${userGuid}/roles/${role}`),

  updateUserRoles: (userGuid, roleAssignments) =>
    apiClient.post(`users/${userGuid}/roles/update`, roleAssignments),

  reactivateUser: (userGuid) =>
    apiClient.post(`users/${userGuid}/reactivate`),
};

const tripAPI = {
  getAllTripsWithBasicInfo: () => apiClient.get('trips'),
  getTrip: (guid) => apiClient.get(`/trips/${guid}`),
  getTripFull: (guid, reason) => apiClient.post(`/trips/${guid}/full`, { reason }),
  createNewTrip: (tripName) => apiClient.post(`/trips/${tripName}`),
  updateTrip: (tripData) => {
    const formData = new FormData();
    Object.entries(tripData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) formData.append(key, value);
    });

    return apiClient.put('trips', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getPriorityKey: (tripGuid) => 
    apiClient.get(`/trips/${tripGuid}/priority`),
  refreshPriorityKey: (tripGuid) => 
    apiClient.post(`/trips/${tripGuid}/priority/refresh`),
  changeTripStatus: (tripGuid, newStatus) =>
    apiClient.patch(`/trips/${tripGuid}/status`, { newStatus }),
  changeSettlementStatus: (tripGuid, status) =>
    apiClient.patch(`/trips/${tripGuid}/settlement-status`, status),
};

const variantAPI = {
  createVariant: (tripGuid, variantData) => {
    const formData = new FormData();
    Object.entries(variantData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (key === "coordinators" && Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    });
    return apiClient.post('variants', formData, {
      params: { tripGuid },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  updateVariant: (variantData) => {
    const formData = new FormData();
    Object.entries(variantData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (key === "coordinators" && Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    });
    return apiClient.put('variants', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteVariant: (variantGuid) => apiClient.delete(`/variants/${variantGuid}`),
};

const elementAPI = {
  createElement: (variantGuid, elementData) =>
    apiClient.post('elements', elementData, { params: { variantGuid } }),
  updateElement: (elementData) =>
    apiClient.put(`elements/${elementData.guid}`, elementData),
  deleteElementFromVariant: (elementGuid, variantGuid) =>
    apiClient.delete(`/elements/${elementGuid}/${variantGuid}`),
  linkElementWithVariant: (elementGuid, variantGuid) =>
    apiClient.post(`/elements/${elementGuid}/${variantGuid}`),
};

const participantsAPI = {
  updateGeneralInfo: (participantGuid, data) =>
    apiClient.put(`/participants/${participantGuid}/general`, data),

  updatePayments: (participantGuid, data) =>
    apiClient.put(`/participants/${participantGuid}/payments`, data),

  updateInsurance: (participantGuid, data) =>
    apiClient.put(`/participants/${participantGuid}/insurance`, data),

  cancelParticipant: (participantGuid) =>
    apiClient.post(`/participants/${participantGuid}/cancel`),

  addCorrections: (participantGuid, corrections) =>
    apiClient.post(`/participants/${participantGuid}/corrections`, corrections),
  
  deleteCorrections: (participantGuid, correctionGuids) =>
    apiClient.post(`/participants/${participantGuid}/corrections/delete`, correctionGuids),

  setAttendance: (updates, tripGuid) => 
    apiClient.post(`/participants/attendance?tripGuid=${tripGuid}`, updates),

  setDiscord: (updates, tripGuid) => 
    apiClient.post(`/participants/discord?tripGuid=${tripGuid}`, updates),
  addBulkCorrections: (data) => apiClient.post("/participants/bulk-corrections", data),
  logView: (participantGuid) => apiClient.get(`/Participants/${participantGuid}/view`),
  logInsuranceView: (participantGuid) => apiClient.get(`/Participants/${participantGuid}/insurance/view`),
};

const registrationAPI = {
  cancelRegistration: (registrationGuid) =>
    apiClient.post(`/registrations/${registrationGuid}/cancel`),

  moveToActive: (registrationGuid) => 
    apiClient.post(`/registrations/${registrationGuid}/moveToActive`),

  confirm: (registrationGuid) => 
    apiClient.post(`/registrations/${registrationGuid}/confirm`),
  getEmailHistory: (registrationGuid) =>
     apiClient.get(`/registrations/${registrationGuid}/email-history`),
  updateReminders: (registrationGuid, data) =>
    apiClient.post(`/registrations/${registrationGuid}/edit-reminders`, data),
  updateAdditionalInfo: (registrationGuid, data) =>
    apiClient.post(`/registrations/${registrationGuid}/edit-additional`, data),
    changePrimaryParticipant: (registrationGuid, payload) => apiClient.post(`/Registrations/${registrationGuid}/change-primary-participant`, payload),
    logView: (registrationGuid) => apiClient.get(`/registrations/${registrationGuid}/view`),
};
 const tripAccessAPI = {
  getUsers: (tripGuid) => apiClient.get(`/trips/${tripGuid}/access/users`),
  getMentors: (tripGuid) => apiClient.get(`/trips/${tripGuid}/access/mentors`),
  getAvailableUsers: (tripGuid) => apiClient.get(`/trips/${tripGuid}/access/available-users`),
  addUser: (tripGuid, userGuid) => apiClient.post(`/trips/${tripGuid}/access/users/${userGuid}`),
  removeUser: (tripGuid, userGuid) => apiClient.delete(`/trips/${tripGuid}/access/users/${userGuid}`),
  addMentor: (tripGuid, userGuid) => apiClient.post(`/trips/${tripGuid}/access/mentors/${userGuid}`),
  removeMentor: (tripGuid, userGuid) => apiClient.delete(`/trips/${tripGuid}/access/mentors/${userGuid}`),
};

const financeAPI = {
  getTripsOverview: () => apiClient.get('/finance/trips'),
  getFinancialReservationsForTrip: (tripGuid) => apiClient.get(`/finance/trips/${tripGuid}/reservations`),
  importReturns: (tripGuid, data) => apiClient.post(`/finance/trips/${tripGuid}/returns`, data),
  changeSettlementStatus: (tripGuid, newStatusId) => apiClient.post(`/finance/trips/${tripGuid}/settlement-status`, newStatusId),
  getSettlementDraft: (tripGuid) =>
    apiClient.get(`/finance/trips/${tripGuid}/settlement-draft`),
  setDraftFullRefund: (tripGuid, data) =>
    apiClient.post(`/finance/trips/${tripGuid}/settlement-draft/full-refund`, data),
  addDraftParticipantAdjustment: (tripGuid, data) =>
    apiClient.post(`/finance/trips/${tripGuid}/settlement-draft/participant-adjustments`, data),
  deleteDraftParticipantAdjustment: (tripGuid, adjustmentId) =>
    apiClient.delete(`/finance/trips/${tripGuid}/settlement-draft/participant-adjustments/${adjustmentId}`),
  addDraftVariantExpense: (tripGuid, data) =>
    apiClient.post(`/finance/trips/${tripGuid}/settlement-draft/variant-expenses`, data),
  deleteDraftVariantExpense: (tripGuid, expenseId) =>
    apiClient.delete(`/finance/trips/${tripGuid}/settlement-draft/variant-expenses/${expenseId}`),
  updateDraftVariantExpense: (tripGuid, expenseId, data) =>
    apiClient.put(`/finance/trips/${tripGuid}/settlement-draft/variant-expenses/${expenseId}`, data),
  setDraftParticipantFullRefund: (tripGuid, data) =>
    apiClient.post(`/finance/trips/${tripGuid}/settlement-draft/participant-full-refund`, data),
deleteDraftParticipantFullRefund: (tripGuid, participantRefundId) =>
  apiClient.delete(`/finance/trips/${tripGuid}/settlement-draft/participant-full-refund/${participantRefundId}`),
getProposedSettlementReturns: (tripGuid, draftUserId = null, mode = 0) =>{
  const params = new URLSearchParams();
  params.append("mode", mode);
  if (draftUserId) {
    params.append("draftUserId", draftUserId);
  }
  return apiClient.get(
    `/Finance/trips/${tripGuid}/settlement-draft/proposed-returns?${params.toString()}`
  );
},
verifyBankStatement: (tripGuid, file) => {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("tripGuid", tripGuid);

  return apiClient.post(`/finance/trips/${tripGuid}/verify-bank-statement`,formData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    });
  },
settlementWorkbookPreview: (tripGuid, draftUserId = null, mode = 0) => {
  const params = new URLSearchParams();
  params.append("mode", mode);
  if (draftUserId) {
    params.append("draftUserId", draftUserId);
  }
  return apiClient.get(
    `/finance/trips/${tripGuid}/settlement-workbook-preview?${params.toString()}`,
    {
      responseType: "arraybuffer",
      transformResponse: [(data) => data],
    }
  );
},
  getPublicSettlementDrafts: (tripGuid) =>
  apiClient.get(`/finance/trips/${tripGuid}/settlement-drafts/public`),
  getSettlementDraftByUser: (tripGuid, userId) =>
  apiClient.get(`/finance/trips/${tripGuid}/settlement-draft/by-user/${userId}`),
  setSettlementDraftVisibility: (tripGuid, isPublic) =>
    apiClient.put(`/finance/trips/${tripGuid}/settlement-draft/visibility`, {
      isPublic,
    }),
}

const subsidiesAPI = {
  getRegistrationByCode: (code) => apiClient.get(`/subsidies/registration/${code}`),
  grantSubsidy: (data) => apiClient.post("/subsidies/grant", data),
};

const viewsAPI = {
  getAvailableViews: () => apiClient.get("/views"),
  getTripsWithVariants: () => apiClient.get("/participantsview/trips"),
  getGroupedParticipants: ({ nameSearch, tripGuid, variantGuid, page, pageSize }) =>
  apiClient.get("/participantsview", {
    params: { search: nameSearch, tripGuid, variantGuid, page, pageSize },
  }),
};

const trustedPersonViewAPI = {
  getSuggestions: (q) =>
    apiClient.get("/trustedpersonview/suggestions", {
      params: { q },
    }),

  getTripsForPerson: (key, dateFrom, dateTo) =>
    apiClient.get("/trustedpersonview/people", {
      params: { key, dateFrom: dateFrom || null, dateTo: dateTo || null },
    }),
};

const insurancesViewAPI = {
  getInternationalVariants: () => apiClient.get('/insurancesview'),
  getParticipantsForVariant: (variantGuid) => apiClient.get(`/insurancesView/${variantGuid}/participants`),
  updateInsurances: (updates) => apiClient.post(`/insurancesview/update`, updates)
};

const bankTransfersAPI = {
  getTransfers: ({search = "", page = 1, pageSize = 15, transferType = "all", categoryId = null, dateFrom = null, dateTo = null, withoutRelatedTrip = false, disablePagination = false, withoutMembershipMember = false,
}) =>
    apiClient.get("/banktransfers", {
        params: {
            search,
            page,
            pageSize,
            type: transferType,
            categoryId,
            dateFrom,
            dateTo,
            withoutRelatedTrip,
            disablePagination,
            withoutMembershipMember
        }
    }),

    synchronize: () => apiClient.post("/banktransfers/synchronize"),

    exportOperationsList: (year, month) =>
    apiClient.get(
        "/banktransfers/verification/month/export-operations",
        {
            params: {
                year,
                month
            },
            responseType: "blob"
        }
    ),

  getCategories: () =>
    apiClient.get("/banktransfers/categories"),

getRelatedTripOptions: () => apiClient.get("/banktransfers/related-trip-options"),

changeCategory: (guid, categoryId) =>
    apiClient.patch(`/banktransfers/${guid}/category`, { categoryId }),

changeRelatedTrip: (guid, relatedTripId) =>
    apiClient.patch(`/banktransfers/${guid}/related-trip`, {
        relatedTripId
    }),
  getMonthVerification: (year, month) =>
    apiClient.get("/banktransfers/verification/month", {
        params: {
            year,
            month
        }
    }),

importMonthStatement: (file, year, month) => {
    const formData = new FormData();

    formData.append("file", file);
    formData.append("year", year);
    formData.append("month", month);

    return apiClient.post(
        "/banktransfers/verification/month/import",
        formData,
        {
            headers: {
                "Content-Type": "multipart/form-data"
            }
        }
    );
},

getFlows: (year) =>
    apiClient.get("/banktransfers/flows", {
        params: {
            year
        }
    }),

  saveManualFlowValue: (data) =>
    apiClient.put("/banktransfers/flows/manual-value", data),

  getMembershipMemberOptions: () =>
    apiClient.get("/Membership/options"),

changeMembershipMember: (guid, membershipMemberId) =>
    apiClient.patch(`/BankTransfers/${guid}/membership-member`, {
        membershipMemberId
    }),

};

 const auditLogsAPI = {
  getUsersLogs: (params) => apiClient.get("/auditLogs", { params }),
};

const registrationsViewAPI = {
  getRegistrations: ({ page, pageSize }) =>
  apiClient.get(`/registrationsView?page=${page}&pageSize=${pageSize}`),
};

const gearViewAPI = {
  getAll: () => apiClient.get('/gear/all'),
  updateConsumables: (payload) => apiClient.post('/gear/update-consumables', payload),
   transfer: ({ toUserId, item, quantity, note }) => apiClient.post("/gear/transfer", { toUserId, item, quantity, note }),
    getMyHistory: ({ take = 100, skip = 0 } = {}) =>
    apiClient.get('/gear/history/my', { params: { take, skip } }),
transferConsumables: (payload) =>
  apiClient.post("/gear/transfer-consumables", payload),

};

const ufgAPI = {
  getMonthSummary: ({ year, month }) =>
    apiClient.get('/ufgregister/summary', {
      params: { year, month },
    }),
  getMonthGrouped: ({ year, month }) =>
    apiClient.get('/ufgregister/month-grouped', { params: { year, month } }),
  exportXlsx: (from, to, all) =>
    apiClient.get("/UFGRegister/export", { params : {from, to, all}, responseType: "blob" }),
  getTfgDashboard: () =>
    apiClient.get("/ufgregister/tfg/dashboard"),

  getTfgPreview: () =>
    apiClient.get("/ufgregister/tfg/preview"),

getTfgExports: (page = 1, pageSize = 10) =>
  apiClient.get("/UFGRegister/tfg/exports", {
    params: { page, pageSize },
  }),

  createTfgExport: () =>
    apiClient.post("/ufgregister/tfg/exports"),

  downloadTfgExport: (exportId) =>
    apiClient.get(`/ufgregister/tfg/exports/${exportId}/file`, {
      responseType: "blob",
    }),

  acceptTfgExport: (exportId) =>
    apiClient.post(`/ufgregister/tfg/exports/${exportId}/accept`),

  rejectTfgExport: (exportId) =>
    apiClient.post(`/ufgregister/tfg/exports/${exportId}/reject`),
};

const discordAPI = {
  channels: {
    getChannelsForTrip: (tripGuid) =>
      apiClient.get(`/discord/${tripGuid}/channels`),

    addChannel: (tripGuid, data) =>
      apiClient.post(`/discord/${tripGuid}/channels`, data),

    sendMessage: (tripGuid, channelId, type) =>
      apiClient.post(`/discord/${tripGuid}/channels/${channelId}/message`, { type }),
  },

  access: {
    getList: (channelId) =>
      apiClient.get(`/discord/access/${channelId}`),

    grantAccess: (channelId, data) =>
      apiClient.post(`/discord/access/${channelId}/grant`, data),

    revokeAccess: (channelId, participantId) =>
  apiClient.post(`/discord/access/${channelId}/revoke`, {
    participantId,
  }),
  revokeInactive: (channelId) =>
    apiClient.post(`/discord/access/${channelId}/revoke-inactive`),
     autoLink: (channelId, matches) =>
      apiClient.post(`/discord/access/${channelId}/autolink`, matches),

     removeUser: (channelId, discordUserId) =>
  apiClient.delete(`/discord/access/${channelId}/user/${discordUserId}`),

  },

   users: {
    getAll: () => apiClient.get(`/discord/users`),
  },
  
};

const systemEmailsAPI = {
  sendBulk: (data) => apiClient.post("/systememails/send-bulk", data),
};

const viewsAccessAPI = {
  getAllAccesses: () => apiClient.get("/viewAccess"),

  grantAccess: ({ viewGuid, userId = null, role = null }) =>
    apiClient.post("/views-admin/accesses", { viewGuid, userId, role }),

  revokeAccess: (accessId) =>
    apiClient.delete(`/views-admin/accesses/${accessId}`),

  setPublic: (viewGuid, isPublic) =>
    apiClient.patch(`/views-admin/definitions/${viewGuid}/public`, { isPublic }),
};

const invoicesAPI = {
getAll: ({ year = null, month = null, status = null, isUploadedToKonektoSmart = null } = {}) =>
    apiClient.get("/invoices", {
        params: {
            year,
            month,
            status,
            isUploadedToKonektoSmart
        }
    }),

  getById: (id) =>
    apiClient.get(`/invoices/${id}`),

sync: (year = null, month = null) =>
    apiClient.post("/invoices/sync", null, {
        params: {
            year,
            month
        }
    }),

  update: (id, data) =>
    apiClient.put(`/invoices/${id}`, data),

  delete: (id) =>
    apiClient.delete(`/invoices/${id}`),

  getAvailableTransfers: () =>
    apiClient.get("/invoices/available-transfers"),

  addBankTransfer: (documentId, bankTransferId) =>
    apiClient.post(`/invoices/${documentId}/transfers/${bankTransferId}`),

  removeBankTransfer: (documentId, bankTransferId) =>
    apiClient.delete(`/invoices/${documentId}/transfers/${bankTransferId}`),

  getReplacementFiles: (id) =>
    apiClient.get(`/invoices/${id}/replacement-files`),

  replaceFile: (id, replacementDocumentId) =>
    apiClient.post(`/invoices/${id}/replace-file`, {
      replacementDocumentId
    }),
getTransfersOverview: ({ year = null, month = null, status = null, categoryId = null } = {}) =>
    apiClient.get("/invoices/transfers", {
        params: {
            year,
            month,
            status,
            categoryId
        }
    }),

setInvoiceRequirement: (
    bankTransferId,
    data
) =>
    apiClient.put(
        `/invoices/transfers/${bankTransferId}/invoice-requirement`,
        data
    ),

    setPaymentConfirmation: (documentId, isConfirmed) =>
    apiClient.put(
        `/invoices/${documentId}/payment-confirmation`,
        { isConfirmed }
    ),
    deleteDuplicate: (documentId, candidateDocumentId) => apiClient.delete(`/invoices/${documentId}/duplicates/${candidateDocumentId}`),
    promoteDuplicate: (documentId, candidateDocumentId, usePreviousFileName = false) => apiClient.post(`/invoices/${documentId}/duplicates/${candidateDocumentId}/promote`, { usePreviousFileName }),
    keepCurrentDuplicate: (documentId, candidateDocumentId, usePreviousFileName = false) => apiClient.post(`/invoices/${documentId}/duplicates/${candidateDocumentId}/keep-current`, { usePreviousFileName }),
    dismissDuplicate: (documentId, candidateDocumentId) => apiClient.post(`/invoices/${documentId}/duplicates/${candidateDocumentId}/dismiss`),
    extractData: (documentId) => apiClient.post(`/invoices/${documentId}/extract-data`),
    getSyncProgress: () => apiClient.get("/invoices/sync-progress")
};

const membershipAPI = {
getMembers: (year, month) =>
    apiClient.get("/Membership", {
        params: { year, month }
    }),

getMember: (guid, year, month) =>
    apiClient.get(`/Membership/${guid}`, {
        params: { year, month }
    }),

  createMember: (data) =>
    apiClient.post("/Membership", data),

  updateMember: (guid, data) =>
    apiClient.put(`/Membership/${guid}`, data),

  resignMember: (guid, resignedAt) =>
    apiClient.patch(`/Membership/${guid}/resign`, resignedAt),

  getMemberOptions: () =>
    apiClient.get("/Membership/options")
};




export {
  apiClient,
  authAPI,
  tripAPI,
  variantAPI,
  elementAPI,
  userAPI,
  participantsAPI,
  registrationAPI,
  tripAccessAPI,
  financeAPI,
  subsidiesAPI,
  viewsAPI,
  insurancesViewAPI,
  bankTransfersAPI,
  auditLogsAPI,
  registrationsViewAPI,
  gearViewAPI,
  ufgAPI,
  discordAPI,
  systemEmailsAPI,
  viewsAccessAPI,
  trustedPersonViewAPI,
  invoicesAPI,
  membershipAPI
};
